import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import QRCode from "qrcode";

const qrCache = new Map();

const styles = StyleSheet.create({

  page: {
    paddingTop: 18,
    paddingBottom: 18,
    paddingLeft: 18,
    paddingRight: 18,
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    flexWrap: "wrap",
    alignContent: "flex-start",
  },

  pageHeader: {
    width: "100%",
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#D1D5DB",
    borderBottomStyle: "solid",
  },

  pageHeaderTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1B2A41",
    textAlign: "center",
  },

  pageHeaderSubtitle: {
    fontSize: 6.5,
    color: "#777777",
    textAlign: "center",
    marginTop: 2,
  },

  label: {
    width: 128,
    height: 125,
    marginRight: 7,
    marginBottom: 7,

    paddingTop: 5,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,

    borderWidth: 1,
    borderColor: "#A7A7A7",
    borderStyle: "solid",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",

    backgroundColor: "#FFFFFF",
  },

  productNameContainer: {
    width: "100%",
    height: 14,
    marginBottom: 2,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  productName: {
    width: "100%",
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    color: "#111827",
    lineHeight: 1.05,
  },

  qrImage: {
    width: 92,
    height: 92,

    marginTop: 0,
    marginBottom: 2,

    alignSelf: "center",
  },

  brand: {
    fontSize: 5.5,
    fontWeight: "bold",
    letterSpacing: 0.6,
    textAlign: "center",
    color: "#1B2A41",
    marginBottom: 1,
  },

  price: {
    fontSize: 5.5,
    textAlign: "center",
    color: "#555555",
    marginBottom: 0,
  },

  unitNumber: {
    fontSize: 4,
    color: "#777777",
    marginTop: 1,
    textAlign: "center",
  },

  token: {
    fontSize: 2.8,
    color: "#AAAAAA",
    marginTop: 1,
    textAlign: "center",
    maxWidth: 115,
  },

  emptyText: {
    fontSize: 10,
    color: "#777777",
    textAlign: "center",
    marginTop: 30,
  },

  qrLoading: {
    width: 92,
    height: 92,

    marginTop: 0,
    marginBottom: 2,

    alignItems: "center",
    justifyContent: "center",

    backgroundColor: "#F9FAFB",

    borderWidth: 1,
    borderColor: "#E5E7EB",
  },

  qrLoadingText: {
    fontSize: 5,
    color: "#999999",
    textAlign: "center",
  },

});

const generateQRCode = async (token) => {

  if (!token) {
    return null;
  }

  const cacheKey = String(token);

  if (qrCache.has(cacheKey)) {
    return qrCache.get(cacheKey);
  }

  try {

    const qrDataUrl =
      await QRCode.toDataURL(
        cacheKey,
        {
          errorCorrectionLevel: "H",
          margin: 1,
          width: 300,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        }
      );

    qrCache.set(
      cacheKey,
      qrDataUrl
    );

    return qrDataUrl;

  } catch (error) {

    console.error(
      "QR GENERATION ERROR:",
      error
    );

    return null;

  }

};

const getUnitCount = (product) => {

  const units =
    Number(
      product?.units_per_carton
    );

  if (
    Number.isFinite(units) &&
    units > 0
  ) {

    return Math.floor(
      units
    );

  }

  return 0;

};

const ProductListQRScanPDF = ({
  products = [],
  selectedPrice = "",
}) => {

  const [
    qrCodes,
    setQrCodes
  ] = useState({});

  const qrProducts = useMemo(() => {

    const seen =
      new Set();

    const result = [];

    for (
      const product
      of products
    ) {

      if (
        !product?.qr_token
      ) {
        continue;
      }

      const token =
        String(
          product.qr_token
        );

      if (
        seen.has(token)
      ) {
        continue;
      }

      seen.add(token);

      result.push({
        id: product.id,
        token,
      });

    }

    return result;

  }, [products]);

  useEffect(() => {

    let cancelled = false;

    const generateAllQRs = async () => {

      if (
        !qrProducts.length
      ) {

        if (
          !cancelled
        ) {

          setQrCodes({});

        }

        return;

      }

      const results =
        await Promise.all(
          qrProducts.map(
            async ({
              id,
              token,
            }) => {

              const qr =
                await generateQRCode(
                  token
                );

              return {
                id,
                token,
                qr,
              };

            }
          )
        );

      if (
        cancelled
      ) {
        return;
      }

      const generated = {};

      for (
        const result
        of results
      ) {

        if (
          result.qr
        ) {

          generated[
            result.id
          ] = result.qr;

        }

      }

      setQrCodes(
        generated
      );

    };

    generateAllQRs();

    return () => {

      cancelled = true;

    };

  }, [qrProducts]);

  const unitLabels = useMemo(() => {

    if (
      !products.length
    ) {

      return [];

    }

    const labels = [];

    for (
      const product
      of products
    ) {

      const unitCount =
        getUnitCount(
          product
        );

      if (
        unitCount <= 0
      ) {
        continue;
      }

      for (
        let unitIndex = 1;
        unitIndex <= unitCount;
        unitIndex++
      ) {

        labels.push({

          product,

          unitIndex,

          unitCount,

        });

      }

    }

    return labels;

  }, [products]);

  const totalLabels =
    unitLabels.length;

  if (
    !products.length
  ) {

    return (

      <Document>

        <Page
          size="A4"
          style={
            styles.page
          }
        >

          <View
            style={
              styles.pageHeader
            }
          >

            <Text
              style={
                styles.pageHeaderTitle
              }
            >
              ZULUWHISKEY PRODUCT QR LABELS
            </Text>

            <Text
              style={
                styles.pageHeaderSubtitle
              }
            >
              No products selected
            </Text>

          </View>

          <Text
            style={
              styles.emptyText
            }
          >
            No products are available
            for this QR label PDF.
          </Text>

        </Page>

      </Document>

    );

  }

  return (

    <Document>

      <Page
        size="A4"
        style={
          styles.page
        }
        wrap
      >

        <View
          style={
            styles.pageHeader
          }
          wrap={false}
        >

          <Text
            style={
              styles.pageHeaderTitle
            }
          >
            ZULUWHISKEY PRODUCT QR LABELS
          </Text>

          <Text
            style={
              styles.pageHeaderSubtitle
            }
          >

            {
              selectedPrice
                ? `Selling Price: $${Number(
                    selectedPrice
                  ).toFixed(2)}`
                : "Product QR Labels"
            }

            {" • "}

            {
              totalLabels
            }

            {" physical unit label(s)"}

          </Text>

        </View>

        {
          unitLabels.map(
            ({
              product,
              unitIndex,
              unitCount,
            }) => {

              const qrImage =
                qrCodes[
                  product.id
                ];

              return (

                <View
                  key={
                    `${product.id}-${unitIndex}`
                  }
                  style={
                    styles.label
                  }
                  wrap={false}
                >

                  <View
                    style={
                      styles.productNameContainer
                    }
                  >

                    <Text
                      style={
                        styles.productName
                      }
                    >
                      {
                        product.name
                      }
                    </Text>

                  </View>

                  {
                    qrImage ? (

                      <Image
                        src={
                          qrImage
                        }
                        style={
                          styles.qrImage
                        }
                      />

                    ) : (

                      <View
                        style={
                          styles.qrLoading
                        }
                      >

                        <Text
                          style={
                            styles.qrLoadingText
                          }
                        >
                          Preparing QR...
                        </Text>

                      </View>

                    )
                  }

                  <Text
                    style={
                      styles.brand
                    }
                  >
                    ZULUWHISKEY
                  </Text>

                  <Text
                    style={
                      styles.price
                    }
                  >
                    ${Number(
                      product.price || 0
                    ).toFixed(2)}
                  </Text>

                  <Text
                    style={
                      styles.unitNumber
                    }
                  >
                    Unit {unitIndex}
                    {" / "}
                    {unitCount}
                  </Text>

                  <Text
                    style={
                      styles.token
                    }
                  >
                    {
                      product.qr_token
                    }
                  </Text>

                </View>

              );

            }
          )
        }

      </Page>

    </Document>

  );

};

export default ProductListQRScanPDF;





// import React, {
//   useEffect,
//   useMemo,
//   useState,
// } from "react";

// import {
//   Document,
//   Page,
//   Text,
//   View,
//   StyleSheet,
//   Image,
// } from "@react-pdf/renderer";

// import QRCode from "qrcode";


// /*
// |--------------------------------------------------------------------------
// | QR CACHE
// |--------------------------------------------------------------------------
// |
// | This lives outside the React component.
// |
// | Once a QR code has been generated for a token,
// | it stays cached and can be reused immediately.
// |
// */

// const qrCache = new Map();


// /*
// |--------------------------------------------------------------------------
// | PDF STYLES
// |--------------------------------------------------------------------------
// */

// const styles = StyleSheet.create({

//   page: {
//     paddingTop: 22,
//     paddingBottom: 22,
//     paddingLeft: 22,
//     paddingRight: 22,

//     backgroundColor: "#FFFFFF",

//     flexDirection: "row",

//     flexWrap: "wrap",

//     alignContent: "flex-start",
//   },


//   pageHeader: {
//     width: "100%",

//     marginBottom: 12,

//     paddingBottom: 6,

//     borderBottomWidth: 1,

//     borderBottomColor: "#D1D5DB",

//     borderBottomStyle: "solid",
//   },


//   pageHeaderTitle: {
//     fontSize: 11,

//     fontWeight: "bold",

//     color: "#1B2A41",

//     textAlign: "center",
//   },


//   pageHeaderSubtitle: {
//     fontSize: 6.5,

//     color: "#777777",

//     textAlign: "center",

//     marginTop: 2,
//   },


//   label: {
//     width: "31.8%",

//     minHeight: 190,

//     marginRight: "1.5%",

//     marginBottom: 10,

//     paddingTop: 7,

//     paddingBottom: 6,

//     paddingLeft: 6,

//     paddingRight: 6,

//     borderWidth: 1,

//     borderColor: "#A7A7A7",

//     borderStyle: "solid",

//     display: "flex",

//     flexDirection: "column",

//     alignItems: "center",

//     justifyContent: "flex-start",

//     backgroundColor: "#FFFFFF",
//   },


//   productNameContainer: {
//     width: "100%",

//     minHeight: 18,

//     marginBottom: 4,

//     display: "flex",

//     alignItems: "center",

//     justifyContent: "center",
//   },


//   productName: {
//     width: "100%",

//     fontSize: 8.5,

//     fontWeight: "bold",

//     textAlign: "center",

//     color: "#111827",

//     lineHeight: 1.15,
//   },


//   qrImage: {
//     width: 120,

//     height: 120,

//     marginTop: 1,

//     marginBottom: 4,

//     alignSelf: "center",
//   },


//   brand: {
//     fontSize: 6.5,

//     fontWeight: "bold",

//     letterSpacing: 0.8,

//     textAlign: "center",

//     color: "#1B2A41",

//     marginBottom: 2,
//   },


//   price: {
//     fontSize: 6.5,

//     textAlign: "center",

//     color: "#555555",

//     marginBottom: 0,
//   },


//   unitNumber: {
//     fontSize: 4.5,

//     color: "#777777",

//     marginTop: 2,

//     textAlign: "center",
//   },


//   token: {
//     fontSize: 3.5,

//     color: "#AAAAAA",

//     marginTop: 2,

//     textAlign: "center",

//     maxWidth: 120,
//   },


//   emptyText: {
//     fontSize: 10,

//     color: "#777777",

//     textAlign: "center",

//     marginTop: 30,
//   },


//   qrLoading: {
//     width: 120,

//     height: 120,

//     marginTop: 1,

//     marginBottom: 4,

//     alignItems: "center",

//     justifyContent: "center",

//     backgroundColor: "#F9FAFB",

//     borderWidth: 1,

//     borderColor: "#E5E7EB",
//   },


//   qrLoadingText: {
//     fontSize: 5,

//     color: "#999999",

//     textAlign: "center",
//   },

// });


// /*
// |--------------------------------------------------------------------------
// | FAST QR GENERATION
// |--------------------------------------------------------------------------
// |
// | IMPORTANT:
// |
// | We do NOT create a QR code for every physical unit.
// |
// | Example:
// |
// | Product:
// |   units_per_carton = 500
// |
// | We generate:
// |
// |   1 QR image
// |
// | Then reuse that image 500 times.
// |
// |--------------------------------------------------------------------------
// */

// const generateQRCode = async (token) => {

//   if (!token) {
//     return null;
//   }


//   const cacheKey = String(token);


//   /*
//   |--------------------------------------------------------------------------
//   | RETURN FROM CACHE
//   |--------------------------------------------------------------------------
//   */

//   if (qrCache.has(cacheKey)) {

//     return qrCache.get(cacheKey);

//   }


//   try {

//     /*
//     |--------------------------------------------------------------------------
//     | Generate at a reasonable source size.
//     |--------------------------------------------------------------------------
//     |
//     | The printed QR is only 120 points.
//     |
//     | 1000x1000 was unnecessarily expensive.
//     |
//     */

//     const qrDataUrl =
//       await QRCode.toDataURL(
//         cacheKey,
//         {
//           errorCorrectionLevel: "H",

//           margin: 2,

//           width: 300,

//           color: {
//             dark: "#000000",

//             light: "#FFFFFF",
//           },
//         }
//       );


//     /*
//     |--------------------------------------------------------------------------
//     | SAVE TO CACHE
//     |--------------------------------------------------------------------------
//     */

//     qrCache.set(
//       cacheKey,
//       qrDataUrl
//     );


//     return qrDataUrl;

//   } catch (error) {

//     console.error(
//       "QR GENERATION ERROR:",
//       error
//     );

//     return null;

//   }

// };


// /*
// |--------------------------------------------------------------------------
// | GET UNIT COUNT
// |--------------------------------------------------------------------------
// |
// | IMPORTANT BUSINESS LOGIC
// |
// | units_per_carton represents the number of individual
// | physical units for this PDF.
// |
// */

// const getUnitCount = (product) => {

//   const units =
//     Number(
//       product?.units_per_carton
//     );


//   if (
//     Number.isFinite(units) &&
//     units > 0
//   ) {

//     return Math.floor(
//       units
//     );

//   }


//   return 0;

// };


// /*
// |--------------------------------------------------------------------------
// | PRODUCT LIST QR SCAN PDF
// |--------------------------------------------------------------------------
// */

// const ProductListQRScanPDF = ({
//   products = [],

//   selectedPrice = "",
// }) => {

//   const [
//     qrCodes,
//     setQrCodes
//   ] = useState({});


//   /*
//   |--------------------------------------------------------------------------
//   | GET UNIQUE PRODUCTS WITH QR TOKENS
//   |--------------------------------------------------------------------------
//   |
//   | This prevents duplicate QR generation.
//   |
//   */

//   const qrProducts = useMemo(() => {

//     const seen =
//       new Set();

//     const result = [];


//     for (
//       const product
//       of products
//     ) {

//       if (
//         !product?.qr_token
//       ) {
//         continue;
//       }


//       const token =
//         String(
//           product.qr_token
//         );


//       if (
//         seen.has(token)
//       ) {
//         continue;
//       }


//       seen.add(token);

//       result.push({
//         id: product.id,
//         token,
//       });

//     }


//     return result;

//   }, [products]);


//   /*
//   |--------------------------------------------------------------------------
//   | GENERATE QR CODES IN PARALLEL
//   |--------------------------------------------------------------------------
//   |
//   | OLD:
//   |
//   | await QR
//   | await QR
//   | await QR
//   | await QR
//   |
//   | NEW:
//   |
//   | Promise.all([
//   |   QR,
//   |   QR,
//   |   QR,
//   |   QR
//   | ])
//   |
//   | This is substantially faster when multiple products
//   | are selected.
//   |
//   */

//   useEffect(() => {

//     let cancelled = false;


//     const generateAllQRs = async () => {

//       if (
//         !qrProducts.length
//       ) {

//         if (
//           !cancelled
//         ) {

//           setQrCodes({});

//         }

//         return;

//       }


//       /*
//       |--------------------------------------------------------------------------
//       | Generate all unique QR codes simultaneously
//       |--------------------------------------------------------------------------
//       */

//       const results =
//         await Promise.all(
//           qrProducts.map(
//             async ({
//               id,
//               token,
//             }) => {

//               const qr =
//                 await generateQRCode(
//                   token
//                 );


//               return {
//                 id,
//                 token,
//                 qr,
//               };

//             }
//           )
//         );


//       if (
//         cancelled
//       ) {
//         return;
//       }


//       const generated = {};


//       for (
//         const result
//         of results
//       ) {

//         if (
//           result.qr
//         ) {

//           generated[
//             result.id
//           ] = result.qr;

//         }

//       }


//       setQrCodes(
//         generated
//       );

//     };


//     generateAllQRs();


//     return () => {

//       cancelled = true;

//     };

//   }, [qrProducts]);


//   /*
//   |--------------------------------------------------------------------------
//   | BUILD PHYSICAL UNIT LABELS
//   |--------------------------------------------------------------------------
//   |
//   | useMemo prevents this huge array from being rebuilt
//   | every time QR state changes.
//   |
//   */

//   const unitLabels = useMemo(() => {

//     if (
//       !products.length
//     ) {

//       return [];

//     }


//     const labels = [];


//     for (
//       const product
//       of products
//     ) {

//       const unitCount =
//         getUnitCount(
//           product
//         );


//       if (
//         unitCount <= 0
//       ) {
//         continue;
//       }


//       for (
//         let unitIndex = 1;
//         unitIndex <= unitCount;
//         unitIndex++
//       ) {

//         labels.push({

//           product,

//           unitIndex,

//           unitCount,

//         });

//       }

//     }


//     return labels;

//   }, [products]);


//   /*
//   |--------------------------------------------------------------------------
//   | TOTAL PHYSICAL LABELS
//   |--------------------------------------------------------------------------
//   */

//   const totalLabels =
//     unitLabels.length;


//   /*
//   |--------------------------------------------------------------------------
//   | EMPTY PRODUCT LIST
//   |--------------------------------------------------------------------------
//   */

//   if (
//     !products.length
//   ) {

//     return (

//       <Document>

//         <Page
//           size="A4"
//           style={
//             styles.page
//           }
//         >

//           <View
//             style={
//               styles.pageHeader
//             }
//           >

//             <Text
//               style={
//                 styles.pageHeaderTitle
//               }
//             >
//               ZULUWHISKEY PRODUCT QR LABELS
//             </Text>


//             <Text
//               style={
//                 styles.pageHeaderSubtitle
//               }
//             >
//               No products selected
//             </Text>

//           </View>


//           <Text
//             style={
//               styles.emptyText
//             }
//           >
//             No products are available
//             for this QR label PDF.
//           </Text>

//         </Page>

//       </Document>

//     );

//   }


//   /*
//   |--------------------------------------------------------------------------
//   | PDF
//   |--------------------------------------------------------------------------
//   */

//   return (

//     <Document>

//       <Page
//         size="A4"
//         style={
//           styles.page
//         }
//         wrap
//       >

//         {/* ================================================================
//             HEADER
//         ================================================================= */}

//         <View
//           style={
//             styles.pageHeader
//           }
//           wrap={false}
//         >

//           <Text
//             style={
//               styles.pageHeaderTitle
//             }
//           >
//             ZULUWHISKEY PRODUCT QR LABELS
//           </Text>


//           <Text
//             style={
//               styles.pageHeaderSubtitle
//             }
//           >

//             {
//               selectedPrice
//                 ? `Selling Price: $${Number(
//                     selectedPrice
//                   ).toFixed(2)}`
//                 : "Product QR Labels"
//             }

//             {" • "}

//             {
//               totalLabels
//             }

//             {" physical unit label(s)"}

//           </Text>

//         </View>


//         {/* ================================================================
//             LABELS
//         ================================================================= */}

//         {
//           unitLabels.map(
//             ({
//               product,
//               unitIndex,
//               unitCount,
//             }) => {

//               const qrImage =
//                 qrCodes[
//                   product.id
//                 ];


//               return (

//                 <View
//                   key={
//                     `${product.id}-${unitIndex}`
//                   }

//                   style={
//                     styles.label
//                   }

//                   wrap={false}
//                 >

//                   {/* ======================================================
//                       PRODUCT NAME
//                   ======================================================= */}

//                   <View
//                     style={
//                       styles.productNameContainer
//                     }
//                   >

//                     <Text
//                       style={
//                         styles.productName
//                       }
//                     >
//                       {
//                         product.name
//                       }
//                     </Text>

//                   </View>


//                   {/* ======================================================
//                       QR CODE
//                   ======================================================= */}

//                   {
//                     qrImage ? (

//                       <Image
//                         src={
//                           qrImage
//                         }

//                         style={
//                           styles.qrImage
//                         }
//                       />

//                     ) : (

//                       <View
//                         style={
//                           styles.qrLoading
//                         }
//                       >

//                         <Text
//                           style={
//                             styles.qrLoadingText
//                           }
//                         >
//                           Preparing QR...
//                         </Text>

//                       </View>

//                     )
//                   }


//                   {/* ======================================================
//                       BRAND
//                   ======================================================= */}

//                   <Text
//                     style={
//                       styles.brand
//                     }
//                   >
//                     ZULUWHISKEY
//                   </Text>


//                   {/* ======================================================
//                       PRICE
//                   ======================================================= */}

//                   <Text
//                     style={
//                       styles.price
//                     }
//                   >
//                     ${Number(
//                       product.price || 0
//                     ).toFixed(2)}
//                   </Text>


//                   {/* ======================================================
//                       UNIT NUMBER
//                   ======================================================= */}

//                   <Text
//                     style={
//                       styles.unitNumber
//                     }
//                   >
//                     Unit {unitIndex}
//                     {" / "}
//                     {unitCount}
//                   </Text>


//                   {/* ======================================================
//                       QR TOKEN
//                   ======================================================= */}

//                   <Text
//                     style={
//                       styles.token
//                     }
//                   >
//                     {
//                       product.qr_token
//                     }
//                   </Text>

//                 </View>

//               );

//             }
//           )
//         }

//       </Page>

//     </Document>

//   );

// };


// export default ProductListQRScanPDF;

